import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
	X,
	Check,
	Loader2,
	ChevronUp,
	ChevronDown,
	ArrowUpToLine,
	Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { useGetServices } from '@/services';
import { useGetProfessionalsDisplayOrder } from '@/services/queries/professional';
import { useReorderProfessionalsDisplay } from '@/services/mutations/professional';

interface ProfessionalDisplayOrderModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const fullName = (p: Professionals.DisplayOrderItem) =>
	[p.name_prefix, p.first_name, p.last_name].filter(Boolean).join(' ');

const categoryLabel = (s: Services.Service) =>
	s.professional_label?.trim() || s.service_name;

/**
 * Ordena, POR CATEGORIA (serviço), a exibição dos profissionais que o usuário
 * B2C realmente vê (autorizados, não banidos, visibilidade pública) na home e
 * no agendamento. Cada categoria tem sua própria ordem; a posição na lista
 * vira a ordem e é salva de uma vez.
 */
export const ProfessionalDisplayOrderModal = ({
	isOpen,
	onClose,
}: ProfessionalDisplayOrderModalProps) => {
	const { data: servicesData, isLoading: isLoadingServices } = useGetServices(
		{
			filters: { active: true, type: 'PROFESSION' },
			enabled: isOpen,
		},
	);
	const services = useMemo(
		() => (Array.isArray(servicesData) ? servicesData : []),
		[servicesData],
	);

	const [selectedServiceId, setSelectedServiceId] = useState<string | null>(
		null,
	);

	// Categoria inicial: a primeira da lista (o admin troca pelas abas).
	useEffect(() => {
		if (!isOpen) {
			setSelectedServiceId(null);
			return;
		}
		if (!selectedServiceId && services.length > 0) {
			setSelectedServiceId(services[0].id);
		}
	}, [isOpen, services, selectedServiceId]);

	const { data, isLoading, isError } = useGetProfessionalsDisplayOrder(
		{ service_id: selectedServiceId ?? undefined },
		{ enabled: isOpen && !!selectedServiceId },
	);
	const [items, setItems] = useState<Professionals.DisplayOrderItem[]>([]);

	const { mutateAsync: reorder, isPending } =
		useReorderProfessionalsDisplay();

	useEffect(() => {
		if (isOpen && data) setItems(data);
	}, [isOpen, data]);

	const originalIds = useMemo(() => (data ?? []).map((p) => p.id), [data]);
	const isDirty = useMemo(
		() =>
			items.length !== originalIds.length ||
			items.some((p, i) => p.id !== originalIds[i]),
		[items, originalIds],
	);

	const move = (from: number, to: number) => {
		if (to < 0 || to >= items.length || from === to) return;
		setItems((prev) => {
			const next = [...prev];
			const [moved] = next.splice(from, 1);
			next.splice(to, 0, moved);
			return next;
		});
	};

	const handleSave = async () => {
		if (!isDirty || isPending || !selectedServiceId) return;
		await reorder({
			ordered_ids: items.map((p) => p.id),
			service_id: selectedServiceId,
		});
	};

	if (!isOpen) return null;

	const selectedService = services.find((s) => s.id === selectedServiceId);
	const iconBtn =
		'flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-500 transition-colors hover:border-stone-400 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-30';

	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative flex max-h-[90vh] w-full max-w-[680px] flex-col overflow-hidden rounded-[2rem] bg-white shadow-2xl duration-200">
				<div className="flex shrink-0 items-start justify-between border-b border-stone-50 px-8 py-6">
					<div>
						<h2 className="text-lg font-black text-stone-900">
							Ordem de exibição por categoria
						</h2>
						<p className="mt-1 text-xs text-stone-400">
							Cada categoria tem sua própria ordem na home e no
							agendamento do usuário.
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-stone-400 transition-colors hover:text-stone-600"
					>
						<X size={20} />
					</button>
				</div>

				{/* Abas de categoria */}
				<div className="shrink-0 border-b border-stone-100 px-8 pt-4">
					{isLoadingServices ? (
						<div className="h-9 w-40 animate-pulse rounded-lg bg-stone-100" />
					) : (
						<div
							className="flex flex-wrap gap-1"
							role="tablist"
							aria-label="Categorias"
						>
							{services.map((s) => {
								const active = s.id === selectedServiceId;
								return (
									<button
										key={s.id}
										type="button"
										role="tab"
										aria-selected={active}
										disabled={
											isPending || (isDirty && !active)
										}
										title={
											isDirty && !active
												? 'Salve ou cancele a ordem atual antes de trocar de categoria'
												: undefined
										}
										onClick={() =>
											setSelectedServiceId(s.id)
										}
										className={cn(
											'rounded-t-lg border-b-2 px-4 py-2 text-[11px] font-black uppercase tracking-widest transition-colors',
											active
												? 'border-stone-900 text-stone-900'
												: 'border-transparent text-stone-400 hover:text-stone-600',
											'disabled:cursor-not-allowed disabled:opacity-50',
										)}
									>
										{categoryLabel(s)}
									</button>
								);
							})}
						</div>
					)}
				</div>

				<div className="flex-1 space-y-4 overflow-y-auto p-8">
					<div className="flex items-start gap-2 rounded-xl bg-stone-50 px-4 py-3 text-[11px] leading-snug text-stone-500">
						<Info size={14} className="mt-0.5 shrink-0" />
						<span>
							Só aparecem os profissionais que o usuário realmente
							vê nesta categoria: autorizados, não banidos e com
							visibilidade pública. Inativos e ocultos ficam de
							fora. Mudanças aqui não afetam as outras categorias.
						</span>
					</div>

					{isLoading && (
						<div className="flex h-40 animate-pulse items-center justify-center text-[11px] font-black uppercase tracking-widest text-stone-400">
							Carregando profissionais...
						</div>
					)}
					{isError && (
						<p className="text-sm font-bold text-rose-500">
							Erro ao carregar a lista de profissionais.
						</p>
					)}
					{!isLoading &&
						!isError &&
						selectedServiceId &&
						items.length === 0 && (
							<p className="py-10 text-center text-sm text-stone-400">
								Nenhum profissional visível ao usuário em{' '}
								<strong className="font-bold text-stone-600">
									{selectedService
										? categoryLabel(selectedService)
										: 'nesta categoria'}
								</strong>
								.
							</p>
						)}

					{items.length > 0 && (
						<ol className="space-y-2">
							{items.map((p, index) => (
								<li
									key={p.id}
									className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white px-3 py-2.5"
								>
									<span className="w-6 shrink-0 text-center text-xs font-black text-stone-400">
										{index + 1}
									</span>
									{p.profile_photo_url ? (
										<img
											src={p.profile_photo_url}
											alt=""
											className="h-9 w-9 shrink-0 rounded-full object-cover"
										/>
									) : (
										<span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 text-xs font-black text-stone-500">
											{p.first_name.charAt(0)}
										</span>
									)}
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-bold text-stone-900">
											{fullName(p)}
										</p>
										<p className="truncate text-[11px] text-stone-400">
											{p.services.length > 0
												? p.services.join(' · ')
												: 'Sem serviço vinculado'}
										</p>
									</div>
									<div className="flex shrink-0 items-center gap-1">
										<button
											type="button"
											title="Mover para o topo"
											aria-label={`Mover ${fullName(
												p,
											)} para o topo`}
											onClick={() => move(index, 0)}
											disabled={index === 0 || isPending}
											className={iconBtn}
										>
											<ArrowUpToLine size={14} />
										</button>
										<button
											type="button"
											title="Subir"
											aria-label={`Subir ${fullName(p)}`}
											onClick={() =>
												move(index, index - 1)
											}
											disabled={index === 0 || isPending}
											className={iconBtn}
										>
											<ChevronUp size={16} />
										</button>
										<button
											type="button"
											title="Descer"
											aria-label={`Descer ${fullName(p)}`}
											onClick={() =>
												move(index, index + 1)
											}
											disabled={
												index === items.length - 1 ||
												isPending
											}
											className={iconBtn}
										>
											<ChevronDown size={16} />
										</button>
									</div>
								</li>
							))}
						</ol>
					)}
				</div>

				<div className="flex shrink-0 items-center justify-end gap-6 border-t border-stone-50 p-8">
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600 disabled:opacity-50"
					>
						{isDirty ? 'Descartar e fechar' : 'Fechar'}
					</button>
					<Button
						type="button"
						onClick={handleSave}
						disabled={!isDirty || isPending}
						text={
							isPending
								? 'Salvando...'
								: selectedService
								  ? `Salvar ordem de ${categoryLabel(
											selectedService,
								    )}`
								  : 'Salvar ordem'
						}
						className={cn(
							'min-w-[160px] rounded-full bg-black px-8 py-2.5 text-sm font-bold text-white shadow-lg hover:bg-neutral-800',
							(!isDirty || isPending) && 'opacity-50',
						)}
						startIcon={
							isPending ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Check className="size-4 stroke-[3]" />
							)
						}
					/>
				</div>
			</div>
		</div>,
		document.body,
	);
};
